/**
 * CreateInvitationModal -- 새 청첩장 생성 모달
 *
 * 커스텀 URL(slug)만 입력, 나머지 정보는 에디터에서 입력
 * 제출 → POST /api/invitations → 에디터로 이동
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { apiFetch, getErrorMessage } from '../../lib/api.js'
import { Modal, Input, Button, Text } from '../ui/index.js'
import styles from './CreateInvitationModal.module.css'

interface CreateInvitationModalProps {
  open: boolean
  onClose: () => void
}

/** 슬러그 입력값을 소문자 + 하이픈만 허용하도록 정규화 */
function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/--+/g, '-')
}

const DEFAULT_TEMPLATE_ID = 'classic-001'

export function CreateInvitationModal({ open, onClose }: CreateInvitationModalProps) {
  const navigate = useNavigate()

  const [slug, setSlug] = useState('')
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const prevOpenRef = useRef(false)
  useEffect(() => {
    // 모달이 열릴 때 초기화
    if (open && !prevOpenRef.current) {
      setSlug('')
      setSlugStatus('idle')
      setError('')
      setSubmitting(false)
    }
    prevOpenRef.current = open
  }, [open])

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeSlug(e.target.value)
    setSlug(normalized)
    setSlugStatus('idle')

    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!normalized || normalized.length < 3) {
      return
    }

    setSlugStatus('checking')
    debounceRef.current = setTimeout(async () => {
      const res = await apiFetch<{ available: boolean }>(
        `/api/invitations/check-slug?slug=${normalized}`,
      )
      if (res.success) {
        setSlugStatus(res.data.available ? 'available' : 'taken')
      } else {
        setSlugStatus('idle')
      }
    }, 300)
  }

  const canSubmit = !submitting && slug.length >= 3 && slugStatus === 'available'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    setError('')

    const body = {
      templateId: DEFAULT_TEMPLATE_ID,
      slug: slug.length >= 3 ? slug : null,
    }

    const res = await apiFetch<{ id: string; url: string }>('/api/invitations', {
      method: 'POST',
      body: JSON.stringify(body),
    })

    if (res.success) {
      onClose()
      navigate(`/studio/editor/${res.data.id}`)
    } else {
      setError(getErrorMessage(res.error, '청첩장 생성에 실패했습니다'))
      setSubmitting(false)
    }
  }

  const origin = window.location.origin
  const shareUrl = slug.length >= 3 ? `${origin}/inv/${slug}` : ''

  return (
    <Modal open={open} onClose={onClose} title="새 청첩장 만들기" size="sm">
      <form onSubmit={handleSubmit} className={styles.form}>
        <div>
          <Input
            label="커스텀 URL (선택)"
            value={slug}
            onChange={handleSlugChange}
            placeholder="minjun-yuna (영문, 숫자, 하이픈만)"
          />
          {shareUrl && (
            <Text variant="caption" style={{ marginTop: '0.25rem', color: 'var(--ds-color-text-tertiary)' }}>
              공유 URL: {shareUrl}
            </Text>
          )}
          <div className={styles.slugFeedback}>
            {slugStatus === 'checking' && (
              <Text variant="caption" style={{ color: 'var(--ds-color-text-secondary)' }}>
                확인 중...
              </Text>
            )}
            {slugStatus === 'available' && (
              <Text variant="caption" style={{ color: 'var(--ds-color-text-success, #16a34a)' }}>
                사용 가능한 URL입니다
              </Text>
            )}
            {slugStatus === 'taken' && (
              <Text variant="caption" style={{ color: 'var(--ds-color-text-danger, #dc2626)' }}>
                이미 사용 중인 URL입니다
              </Text>
            )}
            {!slug && (
              <Text variant="caption" style={{ color: 'var(--ds-color-text-tertiary)' }}>
                비워두면 자동 생성된 URL이 사용됩니다
              </Text>
            )}
          </div>
        </div>

        {error && (
          <Text variant="caption" style={{ color: 'var(--ds-color-text-danger, #dc2626)' }}>
            {error}
          </Text>
        )}

        <Button type="submit" disabled={!canSubmit}>
          {submitting ? '생성 중...' : '청첩장 만들기'}
        </Button>
      </form>
    </Modal>
  )
}
